"use client";

import { Coins, ShoppingCart } from "@repo/ui/components/icons";
import { Button } from "@repo/ui/components/ui/button";
import { QuantityStepper } from "@repo/ui/components/ui/stepper";
import { cn } from "@repo/ui/lib/utils";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

import { addToCart, getProducts, type BuyCombo, type CatalogProduct, type Metal } from "../../../lib/api-client";
import { formatTomanFromRial, METAL_FA, priceLabel } from "../../../lib/product-display";
import { useLivePrices } from "../../../lib/use-live-prices";
import { BarThumb, Choice, Progress, StepButtons, unique } from "./WizardParts";

const STEPS = ["طلا/نقره؟", "عیار و وزن", "انتخاب شمش", "تعداد و خرید"] as const;
type Step = 1 | 2 | 3 | 4;
const MAX_QUANTITY = 100;

const fa = (n: number) => n.toLocaleString("fa-IR");
const fromPrice = (rial: string | null) => (rial === null ? null : `از ${formatTomanFromRial(rial)}`);

/**
 * The quick-buy steps (2.5). Every choice and the current step live in the
 * URL (/buy?metal=gold&fineness=24k&weight=1g&product=<slug>&step=3), pushed
 * with history.pushState so the browser's back button steps back and a link
 * reopens the same step. A choice that no longer fits (sold out meanwhile,
 * or an earlier choice changed) is dropped and the step falls back to it.
 */
export function BuyWizard({ combos }: { combos: BuyCombo[] }) {
  const params = useSearchParams();
  const topRef = useRef<HTMLDivElement>(null);

  // ── Choices from the URL, each checked against the ones before it ──
  const metals = (["gold", "silver"] as const).filter((m) => combos.some((c) => c.metal === m));
  const metal = metals.find((m) => m === params.get("metal"));
  const forMetal = combos.filter((c) => c.metal === metal);
  const finenesses = unique(forMetal.map((c) => c.fineness), (f) => f.slug);
  // One carat only (silver): it's chosen for the visitor.
  const fineness = finenesses.length === 1 ? finenesses[0] : finenesses.find((f) => f.slug === params.get("fineness"));
  const forFineness = forMetal.filter((c) => c.fineness.slug === fineness?.slug);
  const weight = forFineness.find((c) => c.weight.slug === params.get("weight"));

  // ── Step 3's bars, loaded once metal, carat and weight are known ──
  const comboKey = metal && fineness && weight ? `${metal}:${fineness.slug}:${weight.weight.slug}` : null;
  const [bars, setBars] = useState<{ key: string; list: CatalogProduct[] } | null>(null);
  const [barsError, setBarsError] = useState(false);
  const loadedKey = bars?.key;
  useEffect(() => {
    if (!comboKey || loadedKey === comboKey) return;
    let cancelled = false;
    setBarsError(false);
    const [m, f, w] = comboKey.split(":") as [Metal, string, string];
    void getProducts({ metal: m, fineness: f, weight: w, inStock: true, pageSize: 60 }).then(({ status, data }) => {
      if (cancelled) return;
      if (status !== 200 || !data) setBarsError(true);
      else setBars({ key: comboKey, list: data.products });
    });
    return () => {
      cancelled = true;
    };
  }, [comboKey, loadedKey]);
  const list = bars && bars.key === comboKey ? bars.list : null;
  const brands = list ? unique(list.map((p) => p.brand), (b) => b.slug) : [];
  const brandFilter = brands.find((b) => b.slug === params.get("brand"))?.slug;
  const shown = list?.filter((p) => !brandFilter || p.brand.slug === brandFilter) ?? null;
  // A single matching bar is chosen for the visitor.
  const product = list?.length === 1 ? list[0] : list?.find((p) => p.slug === params.get("product"));

  // The furthest step the choices allow; the URL's step can't skip past it.
  const reachable: Step = !metal ? 1 : !fineness || !weight ? 2 : !product ? 3 : 4;
  const asked = Number(params.get("step") ?? 1);
  const wanted = (asked >= 1 && asked <= 4 ? asked : 1) as Step;
  // While step 3's bars load, a step-4 link waits on step 3 rather than jumping back.
  const step: Step = Math.min(wanted, list === null && comboKey && wanted === 4 ? 3 : reachable) as Step;

  function go(patch: Record<string, string | null>, nextStep: Step) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (nextStep > 1) next.set("step", String(nextStep));
    else next.delete("step");
    const qs = next.toString();
    window.history.pushState(null, "", qs ? `/buy?${qs}` : "/buy");
    if (nextStep !== step && topRef.current && topRef.current.getBoundingClientRect().top < 0) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
  const goStep = (s: Step) => go({}, s);

  // ── Live prices for the bars on screen (2.4) ──
  const live = useLivePrices({ updatedAt: null });
  const priceOf = (p: CatalogProduct) => (p.id in live.prices ? live.prices[p.id]! : p.price.priceRial);

  if (combos.length === 0) {
    return (
      <div className="bg-background rounded-card p-8 text-center shadow-sm">
        <p className="font-semibold">در حال حاضر شمشی برای خرید موجود نیست.</p>
        <p className="text-muted-foreground mt-1 text-sm">به‌زودی موجودی تازه اضافه می‌شود.</p>
      </div>
    );
  }

  const summary = [
    metal && { step: 1 as Step, label: "فلز", value: METAL_FA[metal] },
    fineness && step > 1 && { step: 2 as Step, label: "عیار", value: fineness.label },
    weight && step > 2 && { step: 2 as Step, label: "وزن", value: weight.weight.label },
    product && step > 3 && { step: 3 as Step, label: "شمش", value: `${product.brand.name}${list && list.length > 1 ? ` · ${product.packaging.name}` : ""}` },
  ].filter(Boolean) as { step: Step; label: string; value: string }[];

  const canNext = step < reachable;

  return (
    <div ref={topRef} className="scroll-mt-6 grid gap-4 pb-24 sm:pb-0 lg:grid-cols-[1fr_18rem] lg:items-start">
      <div className="flex min-w-0 flex-col gap-4">
        <Progress steps={STEPS} label="مراحل خرید" step={step} reachable={reachable} onPick={goStep} />

        {/* Phones: earlier choices as a row of chips under the progress. */}
        {summary.length > 0 ? (
          <div className="-mx-3 flex gap-2 overflow-x-auto px-3 [scrollbar-width:none] lg:hidden [&::-webkit-scrollbar]:hidden">
            {summary.map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => goStep(s.step)}
                className="bg-background shrink-0 rounded-full px-3 py-1.5 text-xs whitespace-nowrap shadow-sm"
              >
                {/* «عیار ۹۹۹٫۹» already names itself. */}
                {s.value.includes(s.label) ? null : <span className="text-muted-foreground">{s.label}: </span>}
                <span className="font-semibold">{s.value}</span>
              </button>
            ))}
          </div>
        ) : null}

        <section className="bg-background rounded-card p-4 shadow-sm sm:p-6" aria-labelledby="buy-step-title">
          <h2 id="buy-step-title" className="mb-4 font-bold">
            {step === 1 ? "طلا یا نقره؟" : step === 2 ? "عیار و وزن شمش را انتخاب کنید" : step === 3 ? "شمش دلخواه را انتخاب کنید" : "تعداد و ادامه خرید"}
          </h2>

          {step === 1 ? (
            <div className="grid grid-cols-2 gap-3">
              {(["gold", "silver"] as const).map((m) => {
                const own = combos.filter((c) => c.metal === m);
                const grams = own.map((c) => Number(c.weight.grams));
                const lightest = own.find((c) => Number(c.weight.grams) === Math.min(...grams));
                const heaviest = own.find((c) => Number(c.weight.grams) === Math.max(...grams));
                return (
                  <Choice
                    key={m}
                    selected={metal === m}
                    disabled={own.length === 0}
                    onClick={() => go({ metal: m, fineness: null, weight: null, product: null, brand: null }, 1)}
                    className="items-center py-6 text-center"
                  >
                    <span className={cn("flex size-14 items-center justify-center rounded-full", m === "gold" ? "bg-tint-yellow text-ink-yellow" : "bg-muted text-muted-foreground")}>
                      <Coins size={28} />
                    </span>
                    <span className="mt-2 text-base font-bold">{METAL_FA[m]}</span>
                    {own.length ? (
                      <>
                        <span className="text-muted-foreground text-xs">
                          {lightest && heaviest && lightest !== heaviest ? `${lightest.weight.label} تا ${heaviest.weight.label}` : lightest?.weight.label}
                        </span>
                        <span className="text-xs font-medium">{fromPrice(minPrice(own))}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground text-xs">ناموجود</span>
                    )}
                  </Choice>
                );
              })}
            </div>
          ) : null}

          {step === 2 && metal ? (
            <div className="flex flex-col gap-5">
              <div>
                <p className="text-muted-foreground mb-2 text-sm">عیار</p>
                <div className="flex flex-wrap gap-2">
                  {finenesses.map((f) => (
                    <button
                      key={f.slug}
                      type="button"
                      onClick={() => {
                        // Keep the weight if this carat has it too.
                        const keeps = forMetal.some((c) => c.fineness.slug === f.slug && c.weight.slug === weight?.weight.slug);
                        go({ fineness: f.slug, weight: keeps ? weight!.weight.slug : null, product: null, brand: null }, 2);
                      }}
                      aria-pressed={fineness?.slug === f.slug}
                      className={cn(
                        "rounded-full px-4 py-2 text-sm font-medium transition-colors",
                        fineness?.slug === f.slug ? "bg-primary text-primary-foreground" : "bg-surface hover:bg-muted",
                      )}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-muted-foreground mb-2 text-sm">وزن</p>
                {fineness ? (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
                    {forFineness.map((c) => (
                      <Choice key={c.weight.slug} selected={weight?.weight.slug === c.weight.slug} onClick={() => go({ weight: c.weight.slug, product: null, brand: null }, 2)}>
                        <span className="font-bold">{c.weight.label}</span>
                        <span className="text-muted-foreground text-xs">{fromPrice(c.minPriceRial) ?? "قیمت در دسترس نیست"}</span>
                        {c.count > 1 ? <span className="text-muted-foreground text-xs">{fa(c.count)} مدل</span> : null}
                      </Choice>
                    ))}
                  </div>
                ) : (
                  <p className="bg-surface text-muted-foreground rounded-2xl p-4 text-sm">اول عیار را انتخاب کنید.</p>
                )}
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            barsError ? (
              <p className="bg-tint-red text-ink-red rounded-2xl p-4 text-sm">فهرست شمش‌ها دریافت نشد؛ صفحه را دوباره باز کنید.</p>
            ) : !shown ? (
              <p className="text-muted-foreground py-8 text-center text-sm">در حال دریافت شمش‌ها…</p>
            ) : (
              <div className="flex flex-col gap-3">
                {brands.length > 1 ? (
                  <div className="flex flex-wrap gap-2" aria-label="برند">
                    {[{ slug: "", name: "همه برندها" }, ...brands].map((b) => (
                      <button
                        key={b.slug}
                        type="button"
                        onClick={() => go({ brand: b.slug || null }, 3)}
                        aria-pressed={(brandFilter ?? "") === b.slug}
                        className={cn(
                          "rounded-full px-3.5 py-1.5 text-sm transition-colors",
                          (brandFilter ?? "") === b.slug ? "bg-primary text-primary-foreground" : "bg-surface hover:bg-muted",
                        )}
                      >
                        {b.name}
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="grid gap-3 sm:grid-cols-2">
                  {shown.map((p) => (
                    <Choice key={p.id} selected={product?.id === p.id} onClick={() => go({ product: p.slug }, 3)} className="flex-row items-center gap-3 p-2.5">
                      <BarThumb product={p} />
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-start">
                        <span className="line-clamp-2 text-sm leading-6 font-semibold">{p.title}</span>
                        <span className="text-muted-foreground text-xs">
                          {p.brand.name} · {p.packaging.name}
                        </span>
                        <span className={cn("mt-1 rounded-md text-sm font-bold transition-colors duration-700", live.changed.has(p.id) && "bg-tint-yellow", priceOf(p) === null && "text-muted-foreground text-xs font-medium")}>
                          {priceLabel(priceOf(p))}
                        </span>
                      </span>
                    </Choice>
                  ))}
                </div>
              </div>
            )
          ) : null}

          {step === 4 && product ? <Checkout product={product} price={priceOf(product)} flash={live.changed.has(product.id)} /> : null}

          {/* Desktop: back and next under the step. */}
          <div className="mt-6 hidden items-center justify-between gap-3 sm:flex">
            <StepButtons step={step} last={4} canNext={canNext} onBack={() => goStep((step - 1) as Step)} onNext={() => goStep((step + 1) as Step)} />
          </div>
        </section>

        <p className="text-muted-foreground text-xs leading-6">
          فقط شمش‌های موجود نمایش داده می‌شوند. برای دیدن همه شمش‌ها و مقایسه برندها به{" "}
          <Link href="/store" className="text-foreground underline underline-offset-4">
            فروشگاه
          </Link>{" "}
          بروید.
        </p>
      </div>

      {/* Desktop: the summary beside the steps; each row jumps back to its step. */}
      <aside className="bg-background sticky top-28 hidden rounded-card p-5 shadow-sm lg:block" aria-label="خلاصه انتخاب‌ها">
        <h2 className="mb-3 text-sm font-bold">انتخاب‌های شما</h2>
        <dl className="flex flex-col">
          {[
            { label: "فلز", value: metal ? METAL_FA[metal] : null, step: 1 as Step },
            { label: "عیار", value: step > 1 ? fineness?.label : null, step: 2 as Step },
            { label: "وزن", value: step > 2 ? weight?.weight.label : null, step: 2 as Step },
            { label: "برند", value: step > 3 ? product?.brand.name : null, step: 3 as Step },
            { label: "بسته‌بندی", value: step > 3 ? product?.packaging.name : null, step: 3 as Step },
          ].map((row) => (
            <div key={row.label} className="border-border flex items-center justify-between gap-3 border-b py-2.5 text-sm last:border-b-0">
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd>
                {row.value ? (
                  <button type="button" onClick={() => goStep(row.step)} className="font-semibold hover:underline" title="تغییر">
                    {row.value}
                  </button>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </aside>

      {/* Phones: back and next stay in reach, just above the tab bar. */}
      <div className="bg-background/95 fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+5.25rem)] z-20 mx-3 flex items-center justify-between gap-3 rounded-header px-3 py-2.5 shadow-md backdrop-blur-sm sm:hidden">
        <StepButtons step={step} last={4} canNext={canNext} onBack={() => goStep((step - 1) as Step)} onNext={() => goStep((step + 1) as Step)} />
      </div>
    </div>
  );
}

/** Step 4: how many, the total, and on to the cart or straight to checkout. */
function Checkout({ product, price, flash }: { product: CatalogProduct; price: string | null; flash: boolean }) {
  const router = useRouter();
  const max = Math.min(product.availableQuantity, MAX_QUANTITY);
  const [quantity, setQuantity] = useState(1);
  const [state, setState] = useState<"idle" | "adding" | "error">("idle");
  const total = useMemo(() => (price === null ? null : (BigInt(price) * BigInt(quantity)).toString()), [price, quantity]);

  async function add(then: "/cart" | "/checkout") {
    setState("adding");
    const { status } = await addToCart(product.id, quantity).catch(() => ({ status: 0 }));
    if (status === 201) router.push(then);
    else setState("error");
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-surface flex items-center gap-3 rounded-2xl p-2.5">
        <BarThumb product={product} size="size-24" />
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm leading-6 font-semibold">{product.title}</p>
          <p className="text-muted-foreground text-xs">
            {product.brand.name} · {product.packaging.name}
          </p>
          <p className={cn("mt-1 rounded-md text-sm font-bold transition-colors duration-700", flash && "bg-tint-yellow")}>
            {priceLabel(price)} <span className="text-muted-foreground text-xs font-normal">هر عدد</span>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground text-sm">تعداد</span>
        <QuantityStepper value={quantity} onChange={setQuantity} max={max} />
      </div>

      <div className="border-border flex items-center justify-between gap-3 border-t pt-4">
        <span className="font-semibold">جمع</span>
        <span className="text-lg font-bold">{priceLabel(total)}</span>
      </div>
      <p className="text-muted-foreground -mt-2 text-xs">قیمت لحظه‌ای با اجرت؛ مالیات و هزینه ارسال در صورت وجود در سبد خرید اضافه می‌شود.</p>

      {price === null ? <p className="bg-surface text-muted-foreground rounded-2xl p-3 text-sm">قیمت به‌زودی برمی‌گردد؛ خرید تا آن زمان ممکن نیست.</p> : null}
      {state === "error" ? <p className="text-ink-red text-sm">افزودن به سبد انجام نشد؛ دوباره تلاش کنید.</p> : null}

      <div className="grid gap-2 sm:grid-cols-2">
        <Button type="button" size="lg" disabled={price === null} loading={state === "adding"} onClick={() => void add("/checkout")}>
          افزودن و تکمیل خرید
        </Button>
        <Button type="button" size="lg" variant="secondary" disabled={price === null || state === "adding"} onClick={() => void add("/cart")}>
          <ShoppingCart /> افزودن به سبد خرید
        </Button>
      </div>
    </div>
  );
}

function minPrice(list: BuyCombo[]) {
  return list.reduce<string | null>((min, c) => (c.minPriceRial !== null && (min === null || BigInt(c.minPriceRial) < BigInt(min)) ? c.minPriceRial : min), null);
}

